import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import {
	claimDailyMissionsChest,
	claimMissionReward,
	getDailyMissions,
	SvaraDailyMissionsResponse,
	SvaraMissionClaimResponse,
} from '../../services/api';
import { getUserFacingHttpErrorKey } from '../../utils/helpers/httpErrorKey';
import { profileRequest } from '../auth/authActions';
import {
	missionChestClaimRequest,
	missionClaimFailure,
	missionClaimRequest,
	missionClaimSuccess,
	MissionClaimTarget,
	MISSIONS_CHEST_TARGET,
	missionsHubFailure,
	missionsHubRequest,
	missionsHubSuccess,
} from './missionsActions';

const SERVER_CODE_KEYS: Record<string, string> = {
	mission_not_completed: 'T_SITE_MISSIONS_ERROR_NOT_COMPLETED',
	mission_cycle_expired: 'T_SITE_MISSIONS_ERROR_EXPIRED',
	mission_reward_not_payable: 'T_SITE_MISSIONS_ERROR_UNAVAILABLE',
	mission_not_found: 'T_SITE_MISSIONS_ERROR_UNAVAILABLE',
	svara_mission_not_found: 'T_SITE_MISSIONS_ERROR_UNAVAILABLE',
};

const serverCodeOf = (error: any): string | undefined => {
	const body = error?.response?.data;
	if (!body || typeof body !== 'object') return undefined;

	const message = body.message;
	return typeof message === 'string' ? message : undefined;
};

const missionErrorKey = (error: any, fallbackKey: string): string => {
	const code = serverCodeOf(error);
	if (code && SERVER_CODE_KEYS[code]) return SERVER_CODE_KEYS[code];

	const resolved = getUserFacingHttpErrorKey(error, {
		serverUnavailableKey: fallbackKey,
		authKey: fallbackKey,
		networkKey: fallbackKey,
		clientErrorKey: fallbackKey,
		fallbackKey,
	});

	return resolved.startsWith('T_') ? resolved : fallbackKey;
};

function* fetchMissionsHubWorker() {
	try {
		const data: SvaraDailyMissionsResponse = yield call(getDailyMissions);
		yield put(missionsHubSuccess(data));
	} catch (error: any) {
		yield put(missionsHubFailure(missionErrorKey(error, 'T_SITE_MISSIONS_ERROR_LOAD')));
	}
}

function* settleClaim(
	target: MissionClaimTarget,
	request: () => Promise<SvaraMissionClaimResponse>,
) {
	try {
		const data: SvaraMissionClaimResponse = yield call(request);
		yield put(missionClaimSuccess({ target, outcome: data.outcome, grants: data.grants ?? [] }));
		yield put(profileRequest());
	} catch (error: any) {
		yield put(
			missionClaimFailure({
				target,
				key: missionErrorKey(error, 'T_SITE_MISSIONS_ERROR_FAILED'),
			}),
		);
	}

	yield put(missionsHubRequest());
}

function* claimMissionWorker(action: ReturnType<typeof missionClaimRequest>) {
	yield* settleClaim(action.payload.missionId, () =>
		claimMissionReward(action.payload.missionId, action.payload.cycleKey),
	);
}

function* claimChestWorker(action: ReturnType<typeof missionChestClaimRequest>) {
	yield* settleClaim(MISSIONS_CHEST_TARGET, () => claimDailyMissionsChest(action.payload.cycleKey));
}

export function* missionsRootSaga() {
	yield takeLatest(missionsHubRequest.type, fetchMissionsHubWorker);
	yield takeEvery(missionClaimRequest.type, claimMissionWorker);
	yield takeEvery(missionChestClaimRequest.type, claimChestWorker);
}
